import {
  MAX_EXPLICIT_SKILL_IDS,
  MAX_REFUSED_SKILL_NAMES,
  MAX_SKILL_NAME_LENGTH,
  SKILL_NAME_PATTERN,
  SKILL_SELECTION_UUID_PATTERN,
} from "../constants/skills";

// NOTE: NSAI-3308 — the single client-side normalizer for an explicit skill
// selection, mirroring the server's `normalizeRequestedSkillUuids` rule for
// rule: non-strings dropped, trimmed, lower-cased, non-uuid shapes DROPPED
// (never repaired, never substituted with a near match), duplicates collapsed,
// and at most `MAX_EXPLICIT_SKILL_IDS` kept.
//
// This is hygiene, not a security control: the server re-authorizes every uuid
// through the same resolver the model paths use. Its value here is that the
// wire, the composer state and a buffered retry all agree on one shape.
export const normalizeSkillSelectionUuids = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalized: string[] = [];
  for (const value of values) {
    if (typeof value !== "string") {
      continue;
    }
    const candidate = value.trim().toLowerCase();
    if (!SKILL_SELECTION_UUID_PATTERN.test(candidate)) {
      continue;
    }
    if (normalized.includes(candidate)) {
      continue;
    }
    normalized.push(candidate);
    if (normalized.length === MAX_EXPLICIT_SKILL_IDS) {
      break;
    }
  }

  return normalized;
};

// NOTE: The wire form. An empty selection is `undefined` rather than `[]` so a
// message with nothing pinned carries no field at all — the server reads a
// missing field and an empty array identically, and omitting it keeps a
// no-selection payload byte-identical to what clients sent before the picker.
export const toWireSkillSelectionUuids = (
  values: unknown,
): string[] | undefined => {
  const normalized = normalizeSkillSelectionUuids(values);
  return normalized.length > 0 ? normalized : undefined;
};

// NOTE: NSAI-5925 — the names the per-message cap refused, normalized for the
// wire. Mirrors `normalizeSkillSelectionUuids` deliberately: same drop-never-
// repair posture, same dedupe, same bound applied at the one place every send
// passes through.
//
// It differs from the uuid rule in what it is allowed to accept, and each
// difference has a reason:
//
//   * A NAME, not a uuid. A refused mention was never resolved to a skill — that
//     is what "refused" means — so there is no uuid to carry. `resolveSkillCapOverflow`
//     only reports a name the composer's menu already matched against the
//     catalog, so the value is a real skill name rather than arbitrary draft text.
//   * Lower-cased and shape-checked against SKILL_NAME_PATTERN. Canonical skill
//     names are lowercase kebab-case, and a value that is not one cannot be a
//     skill the catalog offered, so it is dropped rather than stored. This is
//     also what keeps a message row from becoming a place to park free text.
//   * Bounded by MAX_REFUSED_SKILL_NAMES, sized to every known mention the
//     composer can refuse: the tail BEYOND the pinned ones.
//
// Hygiene, not a security control: the server normalizes again, because the
// gateway does not validate WebSocket payloads.
export const normalizeRefusedSkillNames = (values: unknown): string[] => {
  if (!Array.isArray(values)) {
    return [];
  }

  const normalized: string[] = [];
  for (const value of values) {
    if (typeof value !== "string") {
      continue;
    }
    const candidate = value.trim().toLowerCase();
    if (candidate.length > MAX_SKILL_NAME_LENGTH) {
      continue;
    }
    if (!SKILL_NAME_PATTERN.test(candidate)) {
      continue;
    }
    if (normalized.includes(candidate)) {
      continue;
    }
    normalized.push(candidate);
    if (normalized.length === MAX_REFUSED_SKILL_NAMES) {
      break;
    }
  }

  return normalized;
};

// NOTE: The wire form, on the same rule as `toWireSkillSelectionUuids`: an empty
// list is `undefined` so a message that refused nothing carries no field, and a
// payload from before this ticket stays byte-identical.
export const toWireRefusedSkillNames = (
  values: unknown,
): string[] | undefined => {
  const normalized = normalizeRefusedSkillNames(values);
  return normalized.length > 0 ? normalized : undefined;
};
